import { create } from 'zustand'

/**
 * Tracks unread state for the Live Chat sidebar (apps/web/src/pages/agent/chats),
 * keyed by live_chats.id (== the conversationId carried on conversation:message:new
 * for a live chat — see useLiveChat.ts). Kept separate from conversationsStore
 * because that store only tracks conversations the agent has already opened;
 * a live chat can go unread before the agent ever visits the tab.
 */
interface LiveChatUnreadStoreState {
  unreadIds: Set<string>
  /** The live chat currently open in LiveChatsPage, or null when the page
   *  isn't mounted / nothing is selected. Read by the socket handler to
   *  decide whether an incoming message should mark unread + toast. */
  selectedId: string | null
  markUnread: (id: string) => void
  markRead: (id: string) => void
  setSelectedId: (id: string | null) => void
}

export const useLiveChatUnreadStore = create<LiveChatUnreadStoreState>()((set) => ({
  unreadIds: new Set(),
  selectedId: null,
  markUnread: (id) =>
    set((s) => {
      if (s.unreadIds.has(id)) return s
      const next = new Set(s.unreadIds)
      next.add(id)
      return { unreadIds: next }
    }),
  markRead: (id) =>
    set((s) => {
      if (!s.unreadIds.has(id)) return s
      const next = new Set(s.unreadIds)
      next.delete(id)
      return { unreadIds: next }
    }),
  setSelectedId: (id) => set({ selectedId: id }),
}))
